import React, { forwardRef, useEffect } from "react";

import classnames from "classnames";

import { Button } from "../Button/Button";
import { Dialog } from "../Dialog";
import { Body2 } from "../Typography/Variants/Body2";

export interface ConfirmDialogProps {
  open?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  className?: string;
  dialogTitle?: string;
  dialogContent?: string;
  confirmBtnText?: string;
  cancelBtnText?: string;
  shouldShowCancelButton?: boolean;
  isLoading?: boolean;
}

export const ConfirmDialog = forwardRef<HTMLDivElement, ConfirmDialogProps>(
  (
    {
      open = false,
      onCancel,
      onConfirm,
      className,
      dialogTitle = "Discard changes?",
      dialogContent = "All unsaved changes will be lost.",
      confirmBtnText = "OK",
      cancelBtnText = "Cancel",
      shouldShowCancelButton = true,
      isLoading = false,
    },
    ref
  ) => {
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Enter" && onConfirm) {
          event.preventDefault();
          onConfirm();
        }
      };

      if (open) {
        window.addEventListener("keydown", handleKeyDown);
      }

      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }, [open, onConfirm]);

    return (
      <Dialog
        ref={ref}
        open={open}
        onClose={onCancel}
        className={classnames("c-confirm-dialog", className)}
        disableCloseOnEsc={isLoading}
      >
        <Dialog.Title>{dialogTitle}</Dialog.Title>
        <Dialog.ContentDivider />
        <Dialog.Content>
          <Body2 lineHeight="loose" color="secondary" whitespace="pre-line">
            {dialogContent}
          </Body2>
        </Dialog.Content>
        <Dialog.ContentDivider />
        <Dialog.Actions>
          <Button
            variant="primary"
            style={{ marginRight: "12px" }}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {confirmBtnText}
          </Button>
          {shouldShowCancelButton ? (
            <Button
              variant="secondary"
              data-action="cancel"
              onClick={onCancel}
              type="button"
              disabled={isLoading}
            >
              {cancelBtnText}
            </Button>
          ) : null}
        </Dialog.Actions>
      </Dialog>
    );
  }
);

ConfirmDialog.displayName = "ConfirmDialog";
